import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { AuthService } from '@auth/services/auth.service';
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive';
import { ThemeService } from '@core/services/theme.service';
import { SidebarStateService } from '@core/services/sidebar-state.service';
import { CoberturaAlertService } from '@core/services/cobertura-alert.service';
import { DrawerModule } from 'primeng/drawer';
import {
  OcorrenciaNotificationService,
  PostoComandoNotification,
} from '@core/services/ocorrencia-notification.service';

interface Breadcrumb { label: string; url: string; }

@Component({
  selector: 'app-top-bar',
  standalone: true,
  imports: [CommonModule, RouterModule, ClickOutsideDirective, DrawerModule],
  templateUrl: './top-bar.component.html',
  styleUrl: './top-bar.component.scss',
})
export class TopBarComponent {
  breadcrumbs: Breadcrumb[] = [];
  userMenuOpen = false;

  showAlertsDrawer = false;

  private readonly router            = inject(Router);
  private readonly route             = inject(ActivatedRoute);
  private readonly auth              = inject(AuthService);
  readonly theme                     = inject(ThemeService);
  readonly sidebar                   = inject(SidebarStateService);
  private readonly coberturaAlertSvc = inject(CoberturaAlertService);
  readonly notificationSvc           = inject(OcorrenciaNotificationService);

  readonly semCobertura$       = this.coberturaAlertSvc.semCobertura$;
  readonly notificacoes$       = this.notificationSvc.notifications$;
  readonly connectionState$    = this.notificationSvc.connectionState$;
  readonly alertCount$ = this.notificationSvc.unreadCount$;


  get userName(): string {
    return this.auth.getUserName() || 'Utilizador';
  }

  get userRole(): string {
    return this.auth.getGrupos()[0] || 'Administrador';
  }

  constructor() {
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.buildBreadcrumbs(this.route.root))
    ).subscribe(bc => this.breadcrumbs = bc);
  }

  get userInitials(): string {
    return this.userName.split(' ')
      .map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }

  toggleUserMenu(): void { this.userMenuOpen = !this.userMenuOpen; }

  logout(): void {
    this.userMenuOpen = false;
    this.auth.logout();
  }
  irParaNotificacao(item: PostoComandoNotification): void {
    this.notificationSvc.markAsRead(item);
    this.showAlertsDrawer = false;
    const destination = this.notificationSvc.destination(item);
    void this.router.navigate(destination.commands, {
      queryParams: destination.queryParams,
    });
  }

  marcarTodasComoLidas(): void {
    this.notificationSvc.markAllAsRead();
  }

  private buildBreadcrumbs(route: ActivatedRoute, url = '', crumbs: Breadcrumb[] = []): Breadcrumb[] {
    for (const child of route.children) {
      if (child.outlet !== 'primary') continue;
      const segment = child.snapshot.url.map(s => s.path).join('/');
      if (segment) url += `/${segment}`;
      const label = child.snapshot.data['breadcrumb'] || this.formatLabel(segment);
      if (label && (!crumbs.length || crumbs[crumbs.length - 1].url !== url)) {
        crumbs.push({ label, url });
      }
      return this.buildBreadcrumbs(child, url, crumbs);
    }
    return crumbs;
  }

  private formatLabel(path: string): string {
    return path.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
  }
}
